import { BookOpen, Volume2Icon, VolumeOffIcon, X } from "lucide-react";

import type { ReviewQuality } from "@/api/smartReview";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";

import type { ReviewQuestion, ReviewRatingOption } from "../_hooks/useSmartReview";

const QUALITY_LABELS: Record<ReviewQuality, string> = {
  again: "다시",
  hard: "어려움",
  normal: "보통",
  easy: "쉬움",
};

const QUALITY_STYLES: Record<ReviewQuality, string> = {
  again: "border-red-200 text-red-600 hover:bg-red-50",
  hard: "border-amber-200 text-amber-700 hover:bg-amber-50",
  normal: "border-blue-200 text-blue-700 hover:bg-blue-50",
  easy: "border-emerald-200 text-emerald-700 hover:bg-emerald-50",
};

function getIntervalLabel(option: ReviewRatingOption) {
  if (option.is_graduated) {
    return "복습 완료";
  }

  return `${option.interval_days}일 후`;
}

type ReviewQuizViewProps = {
  currentQuestion: ReviewQuestion;
  currentIndex: number;
  totalCount: number;
  ratingOptions: ReviewRatingOption[];
  isAnswerRevealed: boolean;
  pendingQuality: ReviewQuality | null;
  submissionError: boolean;
  isMuted: boolean;
  onRevealAnswer: () => void;
  onSelectQuality: (quality: ReviewQuality) => void;
  onExit: () => void;
  onToggleMute: () => void;
};

export function ReviewQuizView({
  currentQuestion,
  currentIndex,
  totalCount,
  ratingOptions,
  isAnswerRevealed,
  pendingQuality,
  submissionError,
  isMuted,
  onRevealAnswer,
  onSelectQuality,
  onExit,
  onToggleMute,
}: ReviewQuizViewProps) {
  return (
    <Card className="flex w-full h-full min-h-0 flex-col overflow-hidden bg-primary-background pt-2 sm:pt-5 sm:mx-auto sm:max-w-4xl sm:rounded-2xl sm:h-fit sm:shadow-lg">
      <CardHeader className="flex items-center justify-between sm:pt-0 px-5 sm:px-4">
        <Button variant="ghost" size="icon" aria-label="복습 나가기" onClick={onExit}>
          <X className="size-5 text-muted-foreground" />
        </Button>

        <div className="flex items-center gap-1.5 rounded-full px-4 py-1.5">
          <BookOpen className="size-3.5 text-muted-foreground" />
          <span className="text-sm font-semibold text-muted-foreground">
            {currentIndex + 1} / {totalCount}
          </span>
        </div>

        <Button variant="ghost" size="icon" aria-label={isMuted ? "소리 켜기" : "소리 끄기"} onClick={onToggleMute}>
          {isMuted ? (
            <VolumeOffIcon className="size-5 text-muted-foreground" />
          ) : (
            <Volume2Icon className="size-5 text-muted-foreground" />
          )}
        </Button>
      </CardHeader>

      <CardContent className="flex flex-1 min-h-0 flex-col items-center gap-6 px-5 sm:px-4 sm:aspect-video sm:max-h-fit">
        <Card className="relative w-full bg-white flex flex-1 flex-col items-center justify-center min-h-64 gap-3 px-6 text-center">
          <span className="text-4xl font-extrabold text-tertiary tracking-wide">{currentQuestion.word}</span>
          {isAnswerRevealed && (
            <span className="text-xl font-semibold text-slate-600 break-keep">{currentQuestion.meaning}</span>
          )}
          {currentQuestion.reviewStage > 0 && (
            <span className="text-xs text-muted-foreground">{currentQuestion.reviewStage}단계 복습</span>
          )}
        </Card>

        {isAnswerRevealed && (
          <div className="w-full text-center">
            <p className="text-sm font-semibold text-muted-foreground">얼마나 잘 기억했나요?</p>
            {submissionError && (
              <p className="mt-1 text-sm font-semibold text-red-500">저장하지 못했어요. 다시 선택해 주세요.</p>
            )}
          </div>
        )}
      </CardContent>

      <CardFooter className="border-t-0 bg-transparent p-5 pb-6">
        {!isAnswerRevealed ? (
          <Button
            type="button"
            size="lg"
            className="w-full rounded-xl py-6 text-base font-semibold"
            onClick={onRevealAnswer}
          >
            뜻 보기
          </Button>
        ) : (
          <div className="grid w-full grid-cols-2 gap-2 sm:grid-cols-4">
            {ratingOptions.map(option => (
              <Button
                key={option.quality}
                type="button"
                variant="outline"
                size="lg"
                disabled={pendingQuality !== null}
                aria-pressed={pendingQuality === option.quality}
                onClick={() => onSelectQuality(option.quality)}
                className={`h-auto min-h-16 flex-col gap-0.5 rounded-xl border-2 bg-white ${QUALITY_STYLES[option.quality]}`}
              >
                <span className="font-bold">{QUALITY_LABELS[option.quality]}</span>
                <span className="text-xs opacity-70">{getIntervalLabel(option)}</span>
              </Button>
            ))}
          </div>
        )}
      </CardFooter>
    </Card>
  );
}
